import React, {useContext, useEffect, useState} from 'react';
import {Button, DatePicker, Descriptions, Input, InputNumber, Popconfirm, Space, Table, Tabs, Tag} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import IsLoading from '../../../../shared/components/IsLoading';
import ErrorPage from '../../../../shared/components/ErrorPage';
import {useAddPartnerSkill, useDeletePartnerCostume, useGetPartnerData, useSetPartnerBond, useSetPartnerInventory, useSetPartnerSkillUsedAt, useSetPartnerState} from '../../usePlayerData';

const PartnerStateTab = ({partnerState, goodsGiftRows, userKey}) => {
    const [form, setForm] = useState({});
    const [giftRemain, setGiftRemain] = useState({});
    const stateMutation = useSetPartnerState();

    useEffect(() => {
        if (!partnerState) return;
        setForm({
            touchCount: partnerState.touchCount,
            helloCount: partnerState.helloCount,
            staminaMax: partnerState.staminaMax,
            charChangeCnt: partnerState.charChangeCnt,
            initializedAt: partnerState.initializedAt,
        });
        const remain = {};
        (goodsGiftRows || []).forEach(g => { remain[g.giftIdx] = g.remain; });
        setGiftRemain(remain);
    }, [partnerState, goodsGiftRows]);

    if (!partnerState) return <div style={{padding: 16}}>파트너 데이터 없음</div>;

    const handleSave = () => {
        stateMutation.mutate({
            userKey,
            touchCount: form.touchCount,
            helloCount: form.helloCount,
            staminaMax: form.staminaMax,
            charChangeCnt: form.charChangeCnt,
            initializedAt: form.initializedAt,
            goodsGiftRemain: giftRemain,
        });
    };

    const setField = (key, value) => setForm(prev => ({...prev, [key]: value}));

    const goodsCols = [
        {title: '선물 인덱스', dataIndex: 'giftIdx', width: 100},
        {
            title: '아이템명',
            dataIndex: 'itemName',
            width: 160,
        },
        {
            title: '아이템 정보',
            key: 'itemInfo',
            render: (_, r) => `타입:${r.itemType} / ID:${r.itemIdx} / 값:${r.itemValue}`,
        },
        {
            title: '잔여 횟수',
            key: 'remain',
            width: 110,
            render: (_, r) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={giftRemain[r.giftIdx] ?? r.remain}
                    onChange={v => setGiftRemain(prev => ({...prev, [r.giftIdx]: v}))}
                    style={{width: 90}}
                />
            ),
        },
    ];

    return (
        <div>
            <Descriptions bordered size="small" column={2} style={{marginBottom: 16}}>
                <Descriptions.Item label="선택된 파트너">{partnerState.selectedPartnerId || '미선택'}</Descriptions.Item>
                <Descriptions.Item label="선택된 배경">{partnerState.selectedBG || '미선택'}</Descriptions.Item>
                <Descriptions.Item label="선호 선물">{partnerState.favGift || '-'}</Descriptions.Item>
                <Descriptions.Item label="생성 시간">{partnerState.created}</Descriptions.Item>
                <Descriptions.Item label="터치 횟수">
                    <InputNumber size="small" min={0} value={form.touchCount}
                        onChange={v => setField('touchCount', v)} style={{width: 110}}/>
                </Descriptions.Item>
                <Descriptions.Item label="인사 터치 횟수">
                    <InputNumber size="small" min={0} value={form.helloCount}
                        onChange={v => setField('helloCount', v)} style={{width: 110}}/>
                </Descriptions.Item>
                <Descriptions.Item label="말랑지수 맥스">
                    <InputNumber size="small" min={0} value={form.staminaMax}
                        onChange={v => setField('staminaMax', v)} style={{width: 110}}/>
                </Descriptions.Item>
                <Descriptions.Item label="일일 캐릭터 변경 횟수">
                    <InputNumber size="small" min={0} value={form.charChangeCnt}
                        onChange={v => setField('charChangeCnt', v)} style={{width: 110}}/>
                </Descriptions.Item>
                <Descriptions.Item label="초기화 시간" span={2}>
                    <Input size="small" value={form.initializedAt}
                        onChange={e => setField('initializedAt', e.target.value)} style={{width: 200}}/>
                </Descriptions.Item>
            </Descriptions>

            {goodsGiftRows?.length > 0 && (
                <>
                    <div style={{marginBottom: 8, fontWeight: 600}}>일일 재화선물 잔여 횟수</div>
                    <Table
                        size="small"
                        dataSource={goodsGiftRows}
                        columns={goodsCols}
                        pagination={false}
                        bordered
                        style={{marginBottom: 16}}
                    />
                </>
            )}

            <Popconfirm
                title="파트너 상태를 저장하시겠습니까?"
                onConfirm={handleSave}
                okText="확인"
                cancelText="취소"
            >
                <Button type="primary" loading={stateMutation.isPending}>저장</Button>
            </Popconfirm>
        </div>
    );
};

const PartnerListTab = ({partners, userKey}) => {
    const [editValues, setEditValues] = useState({});
    const bondMutation = useSetPartnerBond();

    const handleChange = (partnerId, field, value) => {
        setEditValues(prev => ({
            ...prev,
            [partnerId]: {...(prev[partnerId] ?? {}), [field]: value},
        }));
    };

    const getVal = (row, field) => editValues[row.partnerId]?.[field] ?? row[field];

    const columns = [
        {title: '파트너 ID', dataIndex: 'partnerId', width: 100},
        {title: '캐릭터', dataIndex: 'characterName', width: 150},
        {title: '우정 랭크', dataIndex: 'bondRank', width: 90},
        {
            title: '우정도',
            key: 'bond',
            width: 130,
            render: (_, row) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={getVal(row, 'bond')}
                    onChange={v => handleChange(row.partnerId, 'bond', v)}
                    style={{width: 110}}
                />
            ),
        },
        {
            title: '말랑지수',
            key: 'stamina',
            width: 130,
            render: (_, row) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={getVal(row, 'stamina')}
                    onChange={v => handleChange(row.partnerId, 'stamina', v)}
                    style={{width: 110}}
                />
            ),
        },
        {title: '업데이트', dataIndex: 'updated', width: 160},
        {
            title: '저장',
            key: 'action',
            width: 80,
            render: (_, row) => (
                <Popconfirm
                    title="파트너 정보를 수정하시겠습니까?"
                    onConfirm={() => bondMutation.mutate({
                        userKey,
                        partnerId: row.partnerId,
                        bond: getVal(row, 'bond'),
                        stamina: getVal(row, 'stamina'),
                    })}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" type="primary" loading={bondMutation.isPending}>저장</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Table
            size="small"
            dataSource={(partners || []).map(p => ({...p, key: p.partnerId}))}
            columns={columns}
            pagination={false}
            bordered
        />
    );
};

const InventoryTab = ({inventory, userKey}) => {
    const [editValues, setEditValues] = useState({});
    const inventoryMutation = useSetPartnerInventory();

    const getVal = (row) => editValues[row.itemId] ?? row.quantity;

    const columns = [
        {title: '아이템 ID', dataIndex: 'itemId', width: 110},
        {title: '아이템명', dataIndex: 'itemName', width: 180},
        {
            title: '수량',
            key: 'quantity',
            width: 120,
            render: (_, row) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={getVal(row)}
                    onChange={v => setEditValues(prev => ({...prev, [row.itemId]: v}))}
                    style={{width: 100}}
                />
            ),
        },
        {title: '업데이트', dataIndex: 'updated', width: 160},
        {
            title: '저장',
            key: 'action',
            width: 80,
            render: (_, row) => (
                <Popconfirm
                    title="인벤토리 수량을 수정하시겠습니까?"
                    onConfirm={() => inventoryMutation.mutate({
                        userKey,
                        itemId: row.itemId,
                        quantity: getVal(row),
                    })}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" type="primary" loading={inventoryMutation.isPending}>저장</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Table
            size="small"
            dataSource={(inventory || []).map(inv => ({...inv, key: inv.itemId}))}
            columns={columns}
            pagination={false}
            bordered
        />
    );
};

const MissionTab = ({missions, missionUpdated}) => {
    const columns = [
        {title: '미션 ID', dataIndex: 'missionId', width: 90},
        {title: '미션명', dataIndex: 'missionName'},
        {title: '목표 횟수', dataIndex: 'missionCount', width: 100},
        {
            title: '진행 횟수',
            key: 'cnt',
            width: 100,
            render: (_, r) => r.workSide === 0 ? '-' : r.cnt,
        },
        {
            title: '처리',
            dataIndex: 'workSide',
            width: 100,
            render: v => v === 1 ? '서버' : '클라이언트',
        },
        {
            title: '성공',
            dataIndex: 'isSuccess',
            width: 80,
            render: v => <Tag color={v ? 'green' : 'default'}>{v ? '완료' : '미완료'}</Tag>,
        },
    ];

    return (
        <div>
            {missionUpdated && (
                <div style={{marginBottom: 8, color: '#888', fontSize: 12}}>마지막 업데이트: {missionUpdated}</div>
            )}
            <Table
                size="small"
                dataSource={missions || []}
                columns={columns}
                pagination={false}
                bordered
            />
        </div>
    );
};

const SkillTab = ({skills, userKey}) => {
    const [editValues, setEditValues] = useState({});
    const [newSkillType, setNewSkillType] = useState(null);
    const skillMutation = useSetPartnerSkillUsedAt();
    const addSkillMutation = useAddPartnerSkill();

    const getVal = (row) => {
        if (editValues[row.skillType] !== undefined) return editValues[row.skillType];
        return dayjs.unix(row.usedAt);
    };

    const columns = [
        {title: '스킬 타입', dataIndex: 'skillType', width: 100},
        {
            title: '마지막 사용 시간 (KST)',
            key: 'usedAt',
            render: (_, row) => (
                <Space>
                    <DatePicker
                        showTime
                        size="small"
                        value={getVal(row)}
                        onChange={(d) => setEditValues(prev => ({...prev, [row.skillType]: d}))}
                        format="YYYY-MM-DD HH:mm:ss"
                        style={{width: 190}}
                    />
                    <Button
                        size="small"
                        onClick={() => setEditValues(prev => ({
                            ...prev,
                            [row.skillType]: dayjs().subtract(10, 'hour'),
                        }))}
                    >
                        10시간 이전
                    </Button>
                </Space>
            ),
        },
        {title: '생성 시간', dataIndex: 'created', width: 160},
        {
            title: '저장',
            key: 'action',
            width: 80,
            render: (_, row) => (
                <Popconfirm
                    title="스킬 사용 시간을 수정하시겠습니까?"
                    onConfirm={() => {
                        const d = getVal(row);
                        skillMutation.mutate({userKey, skillType: row.skillType, usedAt: d ? d.unix() : row.usedAt});
                    }}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" type="primary" loading={skillMutation.isPending}>저장</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <Table
                size="small"
                dataSource={(skills || []).map(s => ({...s, key: s.skillType}))}
                columns={columns}
                pagination={false}
                bordered
                style={{marginBottom: 16}}
            />
            <Space>
                <InputNumber
                    placeholder="스킬 타입"
                    size="small"
                    min={0}
                    value={newSkillType}
                    onChange={setNewSkillType}
                    style={{width: 130}}
                />
                <Popconfirm
                    title={`스킬 타입 ${newSkillType}을(를) 추가하시겠습니까?`}
                    onConfirm={() => {
                        addSkillMutation.mutate({
                            userKey,
                            skillType: newSkillType,
                            usedAt: dayjs().subtract(10, 'hour').unix(),
                        });
                        setNewSkillType(null);
                    }}
                    okText="확인"
                    cancelText="취소"
                    disabled={newSkillType === null || newSkillType === ''}
                >
                    <Button
                        size="small"
                        type="dashed"
                        disabled={newSkillType === null || newSkillType === ''}
                        loading={addSkillMutation.isPending}
                    >
                        스킬 추가
                    </Button>
                </Popconfirm>
            </Space>
        </div>
    );
};

const CostumeTab = ({costumes, userKey}) => {
    const deleteMutation = useDeletePartnerCostume();

    const columns = [
        {title: '파트너 ID', dataIndex: 'partnerId', width: 100},
        {
            title: '캐릭터',
            key: 'characterName',
            width: 180,
            render: (_, row) => (
                <Space size={4}>
                    {row.characterName}
                    {!row.owned && <Tag color="default">미보유</Tag>}
                </Space>
            ),
        },
        {title: '착용 코스튬 id', dataIndex: 'wornCostumeId', width: 130},
        {title: '코스튬 명칭(세트)', dataIndex: 'wornCostumeName', width: 200},
        {
            title: '보유 코스튬 id',
            key: 'ownedCostumes',
            render: (_, row) => row.ownedCostumes.length === 0 ? '-' : (
                <Space size={[4, 4]} wrap>
                    {row.ownedCostumes.map(c => (
                        <Popconfirm
                            key={c.costumeId}
                            title={`코스튬 ${c.costumeId} (${c.costumeName}) 을(를) 삭제하시겠습니까? 착용중이라면 미착용으로 변경됩니다.`}
                            onConfirm={() => deleteMutation.mutate({userKey, costumeId: c.costumeId})}
                            okText="확인"
                            cancelText="취소"
                        >
                            <Tag
                                color={c.costumeId === row.wornCostumeId ? 'blue' : 'default'}
                                style={{cursor: 'pointer', padding: '4px 10px', fontSize: 13, marginInlineEnd: 0}}
                            >
                                {c.costumeId} ({c.costumeName}) <DeleteOutlined style={{marginLeft: 4}}/>
                            </Tag>
                        </Popconfirm>
                    ))}
                </Space>
            ),
        },
    ];

    return (
        <Table
            size="small"
            dataSource={costumes || []}
            columns={columns}
            pagination={false}
            bordered
        />
    );
};

const MyDreamPartner = () => {
    const {playerData} = useContext(PlayerContext);
    const {data, isLoading, error} = useGetPartnerData({userKey: playerData.userKey});

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    const tabItems = [
        {
            key: 'state',
            label: '파트너 상태',
            children: (
                <PartnerStateTab
                    partnerState={data?.partnerState}
                    goodsGiftRows={data?.goodsGiftRows}
                    userKey={playerData.userKey}
                />
            ),
        },
        {
            key: 'partners',
            label: `파트너 목록 (${data?.partners?.length ?? 0})`,
            children: <PartnerListTab partners={data?.partners} userKey={playerData.userKey}/>,
        },
        {
            key: 'inventory',
            label: `아이템 인벤토리 (${data?.inventory?.length ?? 0})`,
            children: <InventoryTab inventory={data?.inventory} userKey={playerData.userKey}/>,
        },
        {
            key: 'missions',
            label: `미션 (${data?.missions?.length ?? 0})`,
            children: <MissionTab missions={data?.missions} missionUpdated={data?.missionUpdated}/>,
        },
        {
            key: 'skills',
            label: `스킬 (${data?.skills?.length ?? 0})`,
            children: <SkillTab skills={data?.skills} userKey={playerData.userKey}/>,
        },
        {
            key: 'costumes',
            label: '코스튬',
            children: <CostumeTab costumes={data?.costumes} userKey={playerData.userKey}/>,
        },
    ];

    return <Tabs items={tabItems} size="small" style={{padding: '0 8px'}}/>;
};

export default MyDreamPartner;
